import { useState, useEffect, useCallback } from 'react';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'ab3-theme';

function getInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {}
  return 'light';
}

// Module-level singleton state — shared across all hook instances
let currentTheme: Theme = getInitialTheme();
const subscribers = new Set<(t: Theme) => void>();

// Apply on initial load (before any component mounts)
document.documentElement.setAttribute('data-theme', currentTheme);

function applyTheme(t: Theme) {
  currentTheme = t;
  document.documentElement.setAttribute('data-theme', t);
  try {
    localStorage.setItem(STORAGE_KEY, t);
  } catch {}
  subscribers.forEach((fn) => fn(t));
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => currentTheme);

  useEffect(() => {
    subscribers.add(setTheme);
    if (theme !== currentTheme) setTheme(currentTheme);
    return () => {
      subscribers.delete(setTheme);
    };
  }, []);

  // Cross-tab sync via storage event
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (
        e.key === STORAGE_KEY &&
        (e.newValue === 'dark' || e.newValue === 'light')
      ) {
        applyTheme(e.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const toggleTheme = useCallback(() => {
    applyTheme(currentTheme === 'light' ? 'dark' : 'light');
  }, []);

  return { theme, toggleTheme };
}
